// 메인 화면 "전체 상품" 섹션 (작업지시).
// 카테고리 바로가기 아래에 판매 중인 모든 상품을 정렬 탭과 함께 노출한다.
//  - 품절 상품은 서버에서 제외(excludeSoldOut)한다.
//  - 스크롤이 바닥에 닿으면 다음 페이지를 자동 로드(무한 스크롤)한다.
//  - 기본은 정렬 옵션 미선택 = 관리자 지정 진열 순서(sort 미전달). 탭을 누르면 해당 정렬 적용,
//    이미 선택된 탭을 다시 누르면 해제되어 다시 관리자 지정 진열로 돌아온다.
//  - 정렬: 인기순(popular) / 판매량순(sales) / 가격순(price).

import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { ProductSort } from "@oo/shared";
import { listProducts } from "./catalogApi.ts";
import ProductCard from "./ProductCard.tsx";

const PAGE_SIZE = 20;

// 정렬 탭 정의. label 은 사용자 노출 문구.
const SORT_TABS: ReadonlyArray<{ value: ProductSort; label: string }> = [
  { value: "popular", label: "인기순" },
  { value: "sales", label: "판매량순" },
  { value: "price", label: "가격순" },
];

// 메인 전체 상품 목록 섹션.
export default function AllProductsSection() {
  // null = 정렬 옵션 미선택(관리자 지정 진열 순서).
  const [sort, setSort] = useState<ProductSort | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery({
    // 정렬이 바뀌면 queryKey 가 갈려 첫 페이지부터 다시 불러온다.
    queryKey: ["catalog", "all-products", { sort }],
    queryFn: ({ pageParam }) =>
      listProducts({
        // sort 가 null 이면 전달하지 않아 서버 기본(관리자 지정 진열 순서)을 따른다.
        sort: sort ?? undefined,
        excludeSoldOut: true,
        cursor: pageParam as string | undefined,
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // 바닥 센티넬이 보이면 다음 페이지 로드. IntersectionObserver 로 스크롤 비용을 줄인다.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          void query.fetchNextPage();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query]);

  const products = query.data?.pages.flatMap((p) => p.products) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold">전체 상품</h2>
        {total > 0 ? <span className="text-xs text-ink-600">{total}개</span> : null}
      </div>

      {/* 정렬 탭 */}
      <div className="flex gap-1.5" role="tablist" aria-label="상품 정렬">
        {SORT_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={sort === tab.value}
            // 선택된 탭을 다시 누르면 해제(null) -> 관리자 지정 진열로 복귀.
            onClick={() => setSort((cur) => (cur === tab.value ? null : tab.value))}
            className={`min-h-touch rounded-full px-3.5 text-sm font-medium ${
              sort === tab.value
                ? "bg-brand-600 text-white"
                : "border border-ink-300 text-ink-700 hover:bg-ink-100"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="aspect-square animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="rounded border border-dashed border-ink-300 p-6 text-center text-sm text-ink-600">
          판매 중인 상품이 곧 등록됩니다.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          {/* 무한 스크롤 센티넬 + 로딩 표시 */}
          <div ref={sentinelRef} aria-hidden="true" />
          {query.isFetchingNextPage ? (
            <p className="py-4 text-center text-sm text-ink-600">불러오는 중...</p>
          ) : null}
        </>
      )}
    </section>
  );
}
